export interface BrandColors {
  primary: string; // --color-primary: main brand color
  primaryDark: string; // --color-primary-dark: darker variant
  primaryDarker: string; // --color-primary-darker: darkest variant
  accent: string; // --color-accent: TopBar border
  active: string; // --color-active: Navigation active tab
  button: string; // --color-button: Login submit button
  brandDark: string; // --color-brand-dark: Login title gradient, form labels
  brandDarkAlpha: string; // --color-brand-dark-alpha: Login focused label
  headerAccent: string; // --color-header-accent: Header icon hover, divider
}

export interface BrandConfig {
  id: 'eld' | 'protectum';
  name: string;
  logo: string;
  contactEmail: string;
  favicon: string;
  tagline: string;
  pageTitle: string;
  colors: BrandColors;
}

const protectumBrand: BrandConfig = {
  id: 'protectum',
  name: 'Protectum GmbH',
  logo: new URL('../assets/protectum.jpg', import.meta.url).href,
  contactEmail: 'info@protectum.com',
  favicon: '/protectum.ico',
  tagline: 'Nachhaltig wasserdichte Bauwerke',
  pageTitle: 'Protectum Berlin',
  colors: {
    primary: '#33b5c4', // teal/cyan from logo highlight
    primaryDark: '#2a97a4', // darker teal
    primaryDarker: '#1f7a85', // darkest teal
    accent: '#33b5c4', // TopBar border — logo teal
    active: '#5fd4e0', // lighter teal for active nav tab
    button: '#2a97a4', // login button — dark teal
    brandDark: '#3a3f47', // login title/labels — matches logo gray text
    brandDarkAlpha: 'rgba(58, 63, 71, 0.7)', // focused label
    headerAccent: '#2a97a4', // header icon hover
  },
};

const eldBrand: BrandConfig = {
  id: 'eld',
  name: 'ELD GmbH',
  logo: new URL('../assets/eld_logo.gif', import.meta.url).href,
  contactEmail: 'contact@e-l-d.de',
  favicon: '/eld_favicon.png',
  tagline: '',
  pageTitle: 'ELD Berlin',
  colors: {
    primary: '#1a3c34', // dark forest green from logo
    primaryDark: '#142e28', // deeper green
    primaryDarker: '#0e211d', // darkest green
    accent: '#2e7d60', // TopBar border — medium green accent
    active: '#4caf82', // lighter green for active nav tab
    button: '#1a3c34', // login button — logo green
    brandDark: '#1a3c34', // login title/labels — logo green
    brandDarkAlpha: 'rgba(26, 60, 52, 0.7)', // focused label
    headerAccent: '#2e7d60', // header icon hover — medium green
  },
};

export const brands: Record<string, BrandConfig> = {
  protectum: protectumBrand,
  eld: eldBrand,
};

export function detectBrand(): BrandConfig {
  // 1. Check query param (for dev/testing)
  const params = new URLSearchParams(window.location.search);
  const brandParam = params.get('brand');
  if (brandParam && brands[brandParam]) {
    return brands[brandParam];
  }

  // 2. Check localStorage (persisted override)
  const storedBrand = localStorage.getItem('brand');
  if (storedBrand && brands[storedBrand]) {
    return brands[storedBrand];
  }

  // 3. Check hostname (strip non-alphanumerics so e-l-d / e.l.d / eld all match)
  const normalizedHost = window.location.hostname.toLowerCase().replace(/[^a-z0-9]/g, '');
  if (normalizedHost.includes('eld')) {
    return eldBrand;
  }

  // 4. Default to Protectum
  return protectumBrand;
}
